import React, { useState, useEffect } from 'react';
import firebaseService from '../services/firebaseService';
import './DebugPanel.css';

const DebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    firebaseInitialized: false,
    matchCount: 0,
    matches: [],
    error: null,
    localStorageCount: 0,
    timestamp: null
  });

  const refreshDebugInfo = async () => {
    try {
      const info = {
        firebaseInitialized: !!window.firebaseDb,
        timestamp: new Date().toISOString(),
        error: null
      };

      // Try to get matches from Firestore
      try {
        const matches = await firebaseService.getAllMatches();
        info.matchCount = matches.length;
        info.matches = matches.map(m => ({
          id: m.id,
          homeTeam: m.homeTeam,
          awayTeam: m.awayTeam,
          status: m.status,
          date: m.date
        }));
      } catch (error) {
        info.error = error.message;
        info.matchCount = 0;
        info.matches = [];
      }

      // Check localStorage
      const localMatches = firebaseService.getLocalStorageMatches();
      info.localStorageCount = localMatches.length;

      setDebugInfo(info);
    } catch (error) {
      setDebugInfo(prev => ({
        ...prev,
        error: error.message,
        timestamp: new Date().toISOString()
      }));
    }
  };

  useEffect(() => {
    if (isOpen) {
      refreshDebugInfo();
    }
  }, [isOpen]);

  if (!isOpen) {
    return (
      <button 
        className="debug-toggle-btn"
        onClick={() => setIsOpen(true)}
        title="Open Debug Panel"
      >
        🐛
      </button>
    );
  }

  return (
    <div className="debug-panel">
      <div className="debug-header">
        <h3>🐛 Debug Panel</h3>
        <div className="debug-actions">
          <button onClick={refreshDebugInfo}>🔄 Refresh</button>
          <button onClick={() => setIsOpen(false)}>✖ Close</button>
        </div>
      </div>
      
      <div className="debug-content">
        <div className="debug-section">
          <h4>Firebase Status</h4>
          <div className="debug-item">
            <span>Initialized:</span>
            <span className={debugInfo.firebaseInitialized ? 'status-ok' : 'status-error'}>
              {debugInfo.firebaseInitialized ? '✅ Yes' : '❌ No'}
            </span>
          </div>
          <div className="debug-item">
            <span>Last Check:</span>
            <span>{debugInfo.timestamp || 'N/A'}</span>
          </div>
        </div>

        <div className="debug-section">
          <h4>Match Data</h4>
          <div className="debug-item">
            <span>Firestore Matches:</span>
            <span>{debugInfo.matchCount}</span>
          </div>
          <div className="debug-item">
            <span>LocalStorage Matches:</span>
            <span>{debugInfo.localStorageCount}</span>
          </div>
          {debugInfo.error && (
            <div className="debug-item error">
              <span>Error:</span>
              <span>{debugInfo.error}</span>
            </div>
          )}
        </div>

        {debugInfo.matches.length > 0 && (
          <div className="debug-section">
            <h4>Match List ({debugInfo.matches.length})</h4>
            <div className="debug-matches">
              {debugInfo.matches.map((match, index) => (
                <div key={match.id || index} className="debug-match-item">
                  <div><strong>ID:</strong> {match.id}</div>
                  <div><strong>Teams:</strong> {match.homeTeam} vs {match.awayTeam}</div>
                  <div><strong>Status:</strong> {match.status}</div>
                  <div><strong>Date:</strong> {match.date}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="debug-section">
          <h4>Browser Info</h4>
          <div className="debug-item">
            <span>User Agent:</span>
            <span className="small-text">{navigator.userAgent}</span>
          </div>
          <div className="debug-item">
            <span>Online:</span>
            <span>{navigator.onLine ? '✅ Yes' : '❌ No'}</span>
          </div>
          <div className="debug-item">
            <span>Environment:</span>
            <span>{process.env.NODE_ENV || 'unknown'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebugPanel;
